import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BUCKET_LABELS,
  ENTRY_KIND_LABELS,
  PROBLEM_CODES,
  api,
  errorMessage,
  problemCode,
  useApiMutation,
  useApiQuery,
} from "../api";
import { useWalletScope } from "../wallet";
import type { Entry, History, Int64 } from "../api";
import { Badge, Button, Card, EmptyState, Skeleton, Table } from "../components";
import type { TableColumn } from "../components";
import { formatDateTime, formatTokens, isoDateTime } from "../format";
import { useCardEntrance } from "../motion";
import { AsyncBoundary, PageHeader } from "../shell";
import { HistoriqueFiltres } from "./HistoriqueFiltres";
import type { NatureFiltre } from "./HistoriqueFiltres";
import styles from "./HistoriquePage.module.css";

/**
 * Taille de page demandée. C'est déjà le défaut du serveur, mais l'écrire ici
 * rend le comportement lisible : la taille affichée est un choix d'interface,
 * pas une valeur héritée par accident.
 */
const TAILLE_PAGE = 50;

/** Libellés des natures d'objet référencées par une écriture. */
const REF_LABELS: Record<string, string> = {
  pack: "Pack",
  mission: "Projet",
};

/** Marque du pluriel, pour les phrases de comptage. */
function accord(n: number): string {
  return n > 1 ? "s" : "";
}

/**
 * Seau d'origine → seau de destination.
 *
 * C'est la colonne qui fait comprendre le modèle : les mêmes couleurs de seau
 * que sur le dashboard, dans le même ordre de lecture. Le libellé est écrit en
 * toutes lettres à côté de la couleur — un badge n'est jamais seul porteur
 * d'une information, puisque sa teinte ne se lit ni en noir et blanc ni par un
 * daltonien.
 */
function Mouvement({ entree }: { entree: Entry }) {
  return (
    <span className={styles.mouvement}>
      {entree.bucket_from === null ? (
        // `bucket_from` nul, c'est une entrée venue de l'extérieur du
        // portefeuille : un achat, un ajustement. Aucun seau n'est débité,
        // donc aucune couleur de seau ne convient.
        <Badge size="sm">Extérieur</Badge>
      ) : (
        <Badge bucket={entree.bucket_from} size="sm" dot>
          {BUCKET_LABELS[entree.bucket_from]}
        </Badge>
      )}
      <span className={styles.fleche} aria-hidden="true">
        →
      </span>
      {/* La flèche est décorative : sans ce mot, un lecteur d'écran annoncerait
          « Extérieur Disponible » sans dire dans quel sens. */}
      <span className="lde-visually-hidden">vers</span>
      <Badge bucket={entree.bucket_to} size="sm" dot>
        {BUCKET_LABELS[entree.bucket_to]}
      </Badge>
    </span>
  );
}

/**
 * L'objet à l'origine de l'écriture : le pack acheté, la mission imputée, la
 * version de grille figée à l'achat.
 *
 * L'identifiant est tronqué à l'affichage et complet dans l'infobulle : huit
 * caractères suffisent à reconnaître une ligne, trente-six écrasent la colonne.
 */
function Reference({ entree }: { entree: Entry }) {
  const { ref_type, ref_id, rate_version } = entree;
  const aUneReference = ref_type !== null && ref_id !== null;

  if (!aUneReference && rate_version === null) {
    return <span className={styles.absent}>—</span>;
  }

  return (
    <span className={styles.reference}>
      {aUneReference && (
        <>
          <span className={styles.refType}>{REF_LABELS[ref_type] ?? ref_type}</span>
          <code className={styles.refId} title={ref_id}>
            {ref_id.slice(0, 8)}
          </code>
        </>
      )}
      {rate_version !== null && (
        <span className={styles.grilleVersion}>grille v{rate_version}</span>
      )}
    </span>
  );
}

/**
 * Colonnes du grand livre, dans l'ordre de lecture d'une écriture : quand,
 * quoi, d'où vers où, combien, à cause de quoi.
 *
 * Définies hors du composant : elles ne dépendent d'aucun état, et les
 * recréer à chaque rendu ferait repartir la révélation des lignes.
 */
const COLONNES: TableColumn<Entry>[] = [
  {
    key: "id",
    header: "N°",
    numeric: true,
    width: "5rem",
    // `id` est une chaîne décimale et reste une chaîne : c'est aussi le
    // curseur de pagination, et une conversion en `number` lui mangerait ses
    // derniers chiffres au-delà de 2^53 — silencieusement.
    render: (entree) => <span className={styles.numero}>{entree.id}</span>,
  },
  {
    key: "created_at",
    header: "Date",
    width: "11rem",
    render: (entree) => (
      // `<time>` : la date lisible est au format français, la date exacte reste
      // lisible par une machine et par un lecteur d'écran.
      <time dateTime={isoDateTime(entree.created_at)}>
        {formatDateTime(entree.created_at)}
      </time>
    ),
  },
  {
    key: "kind",
    header: "Nature",
    width: "9rem",
    render: (entree) => (
      <span className={styles.nature}>{ENTRY_KIND_LABELS[entree.kind]}</span>
    ),
  },
  {
    key: "mouvement",
    header: "Mouvement",
    render: (entree) => <Mouvement entree={entree} />,
  },
  {
    key: "amount",
    header: "Montant",
    numeric: true,
    width: "9.5rem",
    // Montant non signé, volontairement. Un signe suppose un point de vue —
    // « + » pour qui ? une consommation fait baisser le réservé et monter le
    // consommé — et la colonne « Mouvement » dit déjà le sens sans ambiguïté.
    // Un « −200 » sur une ligne qui ne touche pas le disponible serait faux.
    render: (entree) => (
      <>
        <span>{formatTokens(entree.amount)}</span>{" "}
        <span className={styles.unite}>tokens</span>
      </>
    ),
  },
  {
    key: "reference",
    header: "Référence",
    render: (entree) => <Reference entree={entree} />,
  },
];

/** Ce qui a été chargé au-delà de la première page, et où reprendre. */
type EtatSuite = {
  /** Écritures des pages chargées après la première, dans l'ordre. */
  supplement: Entry[];
  /**
   * Curseur de la page suivante. `null` signifie **fin du journal**, et rien
   * d'autre — ni « inconnu », ni « zéro ». C'est l'unique preuve qu'il n'y a
   * plus rien à lire.
   */
  curseur: Int64 | null;
};

/**
 * Écran « historique du grand livre ».
 *
 * Le grand livre est en ajout seul : rien ne s'y réécrit, une correction est
 * une écriture de plus. C'est l'écran qu'on ouvre quand un chiffre du dashboard
 * surprend, donc il doit se lire — pas s'interpréter.
 *
 * **Pagination par curseur, et non par numéro de page.** Le journal reçoit des
 * écritures pendant qu'on le parcourt : une pagination par décalage
 * (`offset=50`) ferait sauter ou dupliquer des lignes dès qu'un achat
 * s'intercale entre deux pages. Le curseur, lui, désigne une écriture précise —
 * « les écritures d'identifiant strictement inférieur à celle-ci » — et reste
 * juste quoi qu'il arrive en tête de journal.
 */
export function HistoriquePage() {
  const { walletId, links } = useWalletScope();
  const [filtre, setFiltre] = useState<NatureFiltre>("toutes");

  // Première page : une lecture déclarative, avec ses trois états pris en
  // charge par `AsyncBoundary`. Le portefeuille est une dépendance : l'ESN
  // passe d'une entreprise à l'autre sans démonter l'écran.
  const premiere = useApiQuery<History>(
    (options) => api.getWalletHistory(walletId, { limit: TAILLE_PAGE }, options),
    [walletId],
  );

  const [suite, setSuite] = useState<EtatSuite>({ supplement: [], curseur: null });

  // Le curseur de départ vient de la première page ; toute nouvelle première
  // page (bouton « Réessayer ») remet la pile à zéro, sans quoi on
  // concaténerait deux journaux lus à deux instants différents.
  useEffect(() => {
    setSuite({ supplement: [], curseur: premiere.data?.next_before_id ?? null });
  }, [premiere.data]);

  // Les pages suivantes ne sont pas une lecture déclarative : elles sont
  // déclenchées par l'utilisateur, et leur résultat s'ajoute au lieu de
  // remplacer. `useApiQuery` rejouerait l'appel à chaque changement de
  // dépendance et écraserait la pile ; on passe donc par le suivi d'action.
  const { run: chargerPage, pending: chargementSuite, error: erreurSuite } =
    useApiMutation((curseur: Int64) =>
      api.getWalletHistory(walletId, { before_id: curseur, limit: TAILLE_PAGE }),
    );

  const chargerSuite = useCallback(async () => {
    const curseur = suite.curseur;
    if (curseur === null) {
      return;
    }
    const page = await chargerPage(curseur);
    if (page === null) {
      // Le refus est déjà dans `erreurSuite` : il s'affiche sous le bouton,
      // là où l'utilisateur regarde, plutôt que dans un message volant.
      return;
    }
    // Une page pleine qui épuise le journal rend une page suivante **vide** :
    // c'est le fonctionnement normal d'un curseur, pas une anomalie. On se
    // contente d'enregistrer le nouveau curseur — `null` ferme la pagination.
    setSuite((precedent) => ({
      supplement: [...precedent.supplement, ...page.entries],
      curseur: page.next_before_id,
    }));
  }, [chargerPage, suite.curseur]);

  const chargees = useMemo(
    () =>
      premiere.data === null
        ? []
        : [...premiere.data.entries, ...suite.supplement],
    [premiere.data, suite.supplement],
  );

  const comptes = useMemo(() => {
    const total: Record<NatureFiltre, number> = {
      toutes: chargees.length,
      purchase: 0,
      reserve: 0,
      consume: 0,
      release: 0,
      expire: 0,
      adjust: 0,
    };
    for (const entree of chargees) {
      total[entree.kind] += 1;
    }
    return total;
  }, [chargees]);

  const affichees = useMemo(
    () =>
      filtre === "toutes"
        ? chargees
        : chargees.filter((entree) => entree.kind === filtre),
    [chargees, filtre],
  );

  // L'entrée des deux blocs se rejoue à l'arrivée des données : au premier
  // rendu la ref n'est pas encore attachée, puisque `AsyncBoundary` affiche
  // encore le squelette.
  const conteneur = useCardEntrance<HTMLDivElement>({
    dependencies: [premiere.data],
  });

  // `null` est la seule fin de journal. Ni `"0"`, ni la chaîne vide, ni une
  // page rendue vide ne la prouvent.
  const journalComplet = suite.curseur === null;
  const nbChargees = chargees.length;
  const nbAffichees = affichees.length;

  // Un 404 `wallet_not_found` n'est pas un historique vide : le portefeuille
  // lui-même n'existe pas. Deux états différents, deux messages différents —
  // et la distinction se lit sur le code machine, jamais sur le texte du
  // `detail`, qui est traduisible et peut changer sans préavis.
  const portefeuilleInconnu =
    premiere.data === null &&
    problemCode(premiere.error) === PROBLEM_CODES.walletNotFound;

  return (
    <>
      <PageHeader
        eyebrow="Portefeuille"
        title="Historique du grand livre"
        description="Les écritures, de la plus récente à la plus ancienne. Rien ne s'y réécrit : une correction est une écriture de plus."
      />

      {portefeuilleInconnu ? (
        <EmptyState
          icon="🗄"
          title="Ce portefeuille n'existe pas."
          description={
            <>
              Aucun portefeuille ne porte l'identifiant{" "}
              <code className={styles.refId}>{walletId}</code>. Ce n'est pas une
              panne&nbsp;: le service a répondu, et il ne connaît pas cet
              identifiant. Rechargez la page ; si le message persiste,
              communiquez la référence au support.
            </>
          }
        />
      ) : (
        <AsyncBoundary
          state={premiere}
          errorTitle="Historique indisponible"
          skeleton={
            <div className={styles.grille}>
              <Skeleton variant="block" height="7rem" />
              <Table
                columns={COLONNES}
                rows={[]}
                rowKey={() => ""}
                caption="Chargement des écritures du grand livre…"
                loading
                skeletonRows={8}
              />
            </div>
          }
          isEmpty={(page) => page.entries.length === 0}
          empty={
            <EmptyState
              icon="📄"
              title="Ce portefeuille n'a encore aucune écriture."
              description="Il existe bien, mais rien n'y est jamais entré. Le grand livre étant en ajout seul, la première ligne apparaîtra dès le premier achat crédité."
              action={links.achat !== null ? <Link to={links.achat}>Créditer un pack</Link> : undefined}
            />
          }
        >
          {() => (
            <div ref={conteneur} className={styles.grille}>
              <Card data-anim="card" padding="md">
                <HistoriqueFiltres
                  valeur={filtre}
                  onChange={setFiltre}
                  comptes={comptes}
                  complet={journalComplet}
                />
              </Card>

              <div data-anim="card">
                <Table
                  columns={COLONNES}
                  rows={affichees}
                  rowKey={(entree) => entree.id}
                  caption={
                    filtre === "toutes"
                      ? `Écritures du grand livre, de la plus récente à la plus ancienne. ${nbChargees} écriture${accord(nbChargees)} chargée${accord(nbChargees)}.`
                      : `Écritures du grand livre, de la plus récente à la plus ancienne. Filtre « ${ENTRY_KIND_LABELS[filtre]} » : ${nbAffichees} écriture${accord(nbAffichees)} sur les ${nbChargees} chargée${accord(nbChargees)}.`
                  }
                  empty={
                    <EmptyState
                      compact
                      title={
                        filtre === "toutes"
                          ? "Aucune écriture à afficher."
                          : `Aucune écriture de nature « ${ENTRY_KIND_LABELS[filtre]} » parmi celles chargées.`
                      }
                      description={
                        journalComplet
                          ? "Le journal entier est chargé : il n'y en a réellement aucune."
                          : "Le filtre ne porte que sur les écritures déjà chargées. Il en reste à lire : chargez la suite pour élargir la recherche."
                      }
                      action={
                        journalComplet ? undefined : (
                          <Button
                            variant="secondary"
                            onClick={() => void chargerSuite()}
                            loading={chargementSuite}
                          >
                            Charger la suite
                          </Button>
                        )
                      }
                    />
                  }
                  footer={
                    <div className={styles.pagination}>
                      {/* `aria-live` : au clavier, le bouton garde le focus
                          pendant que des lignes s'ajoutent plus haut. Sans
                          annonce, rien ne dit que quelque chose est arrivé. */}
                      <p className={styles.etatPagination} aria-live="polite">
                        {nbChargees} écriture{accord(nbChargees)} chargée
                        {accord(nbChargees)}
                        {filtre !== "toutes" &&
                          `, dont ${nbAffichees} affichée${accord(nbAffichees)} par le filtre`}
                        .
                      </p>

                      {journalComplet ? (
                        <Badge tone="success" dot>
                          Tout le journal est chargé
                        </Badge>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => void chargerSuite()}
                          loading={chargementSuite}
                        >
                          Charger la suite
                        </Button>
                      )}

                      {erreurSuite !== null && (
                        <div className={styles.erreurPagination} role="alert">
                          <span>{errorMessage(erreurSuite)}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void chargerSuite()}
                            loading={chargementSuite}
                          >
                            Réessayer
                          </Button>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          )}
        </AsyncBoundary>
      )}
    </>
  );
}
